"use client";

import { useEffect, useState } from "react";
import { useBrokersStore } from "@/lib/brokers/store";
import { Broker } from "@/lib/brokers/types";
import { isVybeBrandLogoUrl, isVybeCompanyName } from "@/lib/accounts/brokerLogo";
import { VybeLogo } from "./VybeLogo";

interface BrokerLogoProps {
  broker?: Broker;
  companyName?: string;
  /** Explicit logo (URL or data-URI) from the gateway catalog; takes precedence over the brokers store. */
  logoUrl?: string | null;
  className?: string;
  logoClassName?: string;
}

/**
 * Timed retries after a failed image load, on top of the `online` / `visibilitychange` triggers.
 * A remote logo that failed once on an iPhone PWA (suspended webview, cold start before the
 * network was up) used to stay a grey initials tile for the life of the component; a failed load
 * is a moment, not a verdict.
 */
const RETRY_DELAYS_MS = [3000, 10000, 30000];

export function BrokerLogo({
  broker,
  companyName,
  logoUrl,
  className = "w-12 h-12",
  logoClassName = "w-10 h-10",
}: BrokerLogoProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const getBrokerByCompanyName = useBrokersStore(
    (state) => state.getBrokerByCompanyName
  );

  // A new logo gets a clean slate — the latched error used to outlive the account it belonged to,
  // so one broken tile made every account rendered through the same instance show initials.
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [logoUrl]);

  useEffect(() => {
    if (!imageError) return;
    const retry = () => {
      setImageError(false);
      setImageLoading(true);
      setAttempt((a) => a + 1);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") retry();
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", onVisible);
    const timer =
      attempt < RETRY_DELAYS_MS.length
        ? window.setTimeout(retry, RETRY_DELAYS_MS[attempt])
        : undefined;
    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", onVisible);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [imageError, attempt]);

  // Our own mark is bundled (VybeLogo) — never fetched, so it cannot fail. Matches the gateway's
  // hosted /brand url, the data-URI form, and a Vybe-company account that carries no logo at all.
  const ownMark =
    isVybeBrandLogoUrl(logoUrl) ||
    (!logoUrl && !broker && isVybeCompanyName(companyName));
  if (ownMark) {
    return (
      <div
        className={`${className} rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden`}
      >
        <VybeLogo decorative className={`${logoClassName} text-foreground`} />
      </div>
    );
  }

  // An explicit catalog logo wins (e.g. ViiSync companies that aren't in the brokers store).
  if (logoUrl && !imageError) {
    return (
      <div className={`${className} rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden`}>
        <img
          key={attempt}
          src={logoUrl}
          alt={companyName ?? ""}
          className={`${logoClassName} object-contain`}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Get broker by company name if not provided directly
  const brokerData =
    broker || (companyName ? getBrokerByCompanyName(companyName) : null);

  if (!brokerData) {
    const displayName = companyName || "??";
    return (
      <div
        className={`${className} rounded-lg bg-gray-700 flex items-center justify-center font-bold text-sm`}
      >
        {displayName.substring(0, 2).toUpperCase()}
      </div>
    );
  }

  if (brokerData.logoType === "text" || !brokerData.logoUrl || imageError) {
    return (
      <div
        className={`${className} rounded-lg bg-gray-700 flex items-center justify-center font-bold text-sm`}
      >
        {brokerData.logo}
      </div>
    );
  }

  return (
    <div
      className={`${className} rounded-lg ${
        imageLoading ? "border border-gray-600" : "border border-gray-700"
      } flex items-center justify-center`}
    >
      <img
        key={attempt}
        src={brokerData.logoUrl}
        alt={brokerData.fullName}
        className={`${logoClassName} object-contain ${
          imageLoading ? "opacity-0" : "opacity-100"
        } transition-opacity`}
        onError={() => setImageError(true)}
        onLoad={() => setImageLoading(false)}
      />
    </div>
  );
}
